import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, MoreHorizontal, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  fetchClass,
  fetchInstanceRsvpEligibility,
  fetchInstanceRsvps,
  fetchSchool,
  followClass,
  unfollowClass,
  resolveClassByPath,
  rsvpToInstance,
  updateInstance,
} from "../lib/schools";
import { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ReadableCard, ReadableNavButton } from "../components/Sticker";
import FormLabel from "../components/ui/FormLabel";
import VenueInput, { VENUE_NONE, venueIdFromSelect } from "../components/VenueInput";
import { FieldHint, FORM_STYLE_CARD_CLASS } from "../components/ui/FormSection";
import DetailPageNav from "../components/ui/public/DetailPageNav";
import DetailHeroCard, { DetailMetaRow } from "../components/ui/public/DetailHero";
import ClassHeroCta, { getClassStickyCtaState } from "../components/details/ClassHeroCta";
import EventStickyCta from "../components/ui/public/StickyCta";
import LinkifiedText from "../components/LinkifiedText";
import {
  addDays,
  isUpcomingInstance,
  resolveInstanceVenue,
  shiftWeekAnchor,
  startOfLocalDay,
  upcomingInNextWeek,
} from "../lib/classSessionHelpers";
import { formatPreviewNames, formatRoleCounts, needsClassProfileTodos } from "../lib/classGuestList";
import { loadDancerProfile } from "../lib/dancerProfile";
import AuthModal from "../components/AuthModal";
import { stashPendingAuthAction, consumePendingAuthAction } from "../lib/pendingAuthAction";
import HostThemedShell from "../components/ui/public/HostThemedShell";
import { DEFAULT_HOST_THEME, normalizeHostTheme } from "../lib/hostThemes";
import { listingDrawerBackground } from "../lib/listingDrawer";
import { resolveClassRsvpGate } from "../lib/classRsvpGate";
import {
  classPublicHref as buildClassPublicHref,
  classSessionDateKey,
  pickInstanceForDate,
} from "../lib/classLinks";

const ClassRsvpModal = lazy(() => import("../components/ClassRsvpModal"));
const HostAddStudentModal = lazy(() => import("../components/HostAddStudentModal"));
const ClassRsvpTodosDrawer = lazy(() => import("../components/ClassRsvpTodosDrawer"));
const ClassRsvpGateModal = lazy(() => import("../components/ClassRsvpGateModal"));

function ClassPageLoadingSkeleton() {
  return (
    <div className="min-h-screen max-w-page mx-auto px-4 pt-5 pb-6" aria-busy="true" aria-label="Loading class">
      <div className="animate-pulse space-y-5">
        <div className="flex items-center justify-between gap-2">
          <div className="h-11 w-28 rounded-md2 bg-black/[0.06]" />
          <div className="h-11 w-16 rounded-md2 bg-black/[0.06]" />
        </div>
        <div className="h-52 sm:h-60 rounded-card bg-black/[0.06]" />
        <div className="h-7 w-48 rounded bg-black/[0.06]" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-[72px] rounded-card bg-black/[0.06]" />
          ))}
        </div>
      </div>
      <p className="sr-only">Loading class…</p>
    </div>
  );
}

function ClassActionMenu({ editHref }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector('[role="menuitem"]');
    first?.focus();
  }, [open]);

  if (!editHref) return null;

  return (
    <div className="relative" ref={ref}>
      <ReadableNavButton
        type="button"
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="More actions"
        className="px-2.5 w-11"
        data-testid="class-actions-menu-btn"
      >
        <MoreHorizontal className="w-5 h-5" strokeWidth={2.5} aria-hidden />
      </ReadableNavButton>
      {open ? (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 top-full mt-1 z-30 min-w-[200px] rounded-md2 bg-white border border-black/[0.08] shadow-[0_8px_30px_rgba(0,0,0,0.08)] py-1"
          data-testid="class-actions-menu"
        >
          <Link
            role="menuitem"
            to={editHref}
            onClick={() => setOpen(false)}
            className="w-full text-left px-3 py-2.5 min-h-[44px] font-ui text-[14px] font-medium hover:bg-cream focus-visible:bg-cream focus-visible:outline-offset-[-2px] flex items-center gap-2 whitespace-nowrap"
            data-testid="edit-class-menu-link"
          >
            <Pencil className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
            Edit class
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function formatClock(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatSessionRange(inst) {
  const start = inst.effective_datetime || inst.datetime;
  const end = inst.effective_end_datetime;
  if (!start) return "";
  const dayPart = new Date(start).toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  if (!end) return `${dayPart} · ${formatClock(start)}`;
  return `${dayPart} · ${formatClock(start)} – ${formatClock(end)}`;
}

function formatChipWhen(inst) {
  const start = inst.effective_datetime || inst.datetime;
  if (!start) return "";
  return new Date(start).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function endTimeLabel(startHHMM, durationMinutes) {
  const [h, m] = String(startHHMM || "19:00").split(":").map(Number);
  const total = (h || 0) * 60 + (m || 0) + (Number(durationMinutes) || 60);
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  const d = new Date();
  d.setHours(eh, em, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function timeFromIso(iso) {
  if (!iso) return "19:00";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function sessionsInWeekOffset(instances, weekOffset, nowMs = Date.now()) {
  if (weekOffset === 0) return upcomingInNextWeek(instances, nowMs);
  const anchor = shiftWeekAnchor(startOfLocalDay(new Date(nowMs)), weekOffset);
  const end = addDays(anchor, 7);
  return (instances || []).filter((inst) => {
    if (!inst || inst.status === "CANCELLED") return false;
    const t = new Date(inst.effective_datetime || inst.datetime).getTime();
    if (Number.isNaN(t)) return false;
    return t >= anchor.getTime() && t < end.getTime();
  });
}

const DURATION_OPTIONS = [
  { value: 45, label: "45 min" },
  { value: 60, label: "1 hour" },
  { value: 75, label: "1 hr 15" },
  { value: 90, label: "1 hr 30" },
  { value: 120, label: "2 hours" },
];

export default function ClassPage() {
  const { schoolSlug, classSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const embedded = Boolean(listingDrawerBackground(location));
  const { account } = useAuth();
  const heroRef = useRef(null);
  const [heroScrolledPast, setHeroScrolledPast] = useState(false);

  const [resolvedClassId, setResolvedClassId] = useState(null);
  const [data, setData] = useState(null);
  const [schoolVenues, setSchoolVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [guests, setGuests] = useState([]);
  const [guestListMeta, setGuestListMeta] = useState({ mode: "privacy", counts: null, preview_names: [] });
  const [showRsvp, setShowRsvp] = useState(false);
  const [showHostAdd, setShowHostAdd] = useState(false);
  const [rsvpGate, setRsvpGate] = useState(null);
  const [showProfileTodos, setShowProfileTodos] = useState(false);
  const [profileTodoRsvp, setProfileTodoRsvp] = useState(null);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [rsvpAwaitingEligibility, setRsvpAwaitingEligibility] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [followAuthOpen, setFollowAuthOpen] = useState(false);
  const pendingFollowAfterAuthRef = useRef(false);
  const pendingRsvpClickRef = useRef(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editTime, setEditTime] = useState("19:00");
  const [editDuration, setEditDuration] = useState(60);
  const [editVenueId, setEditVenueId] = useState(VENUE_NONE);
  const [saving, setSaving] = useState(false);
  const autoRsvpHandled = useRef(false);

  const classId = resolvedClassId || null;
  const dateParam = searchParams.get("date");
  const rsvpIntent = searchParams.get("rsvp") === "1";

  const publicClassHref = useCallback((opts = {}) => {
    const cls = data?.class;
    const school = data?.school;
    return buildClassPublicHref(
      {
        schoolSlug: schoolSlug || school?.slug,
        classSlug: classSlug || cls?.slug,
        public_path: cls?.public_path,
      },
      opts,
    );
  }, [data, schoolSlug, classSlug]);

  const reloadEligibility = useCallback(async (instanceId) => {
    if (!instanceId) {
      setEligibility(null);
      return null;
    }
    try {
      const result = await fetchInstanceRsvpEligibility(instanceId);
      const withId = { ...result, instance_id: instanceId };
      setEligibility(withId);
      return withId;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  }, []);

  async function reload() {
    if (!classId) return null;
    const result = await fetchClass(classId);
    setData(result);
    // Admin venue picker needs the full school list (class GET no longer ships venues[]).
    if (result?.is_admin && result?.school?.id) {
      try {
        const schoolData = await fetchSchool(result.school.id);
        setSchoolVenues(schoolData.venues || []);
      } catch {
        setSchoolVenues([]);
      }
    } else {
      setSchoolVenues([]);
    }
    return result;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let id = null;
        if (schoolSlug && classSlug) {
          const resolved = await resolveClassByPath(schoolSlug, classSlug);
          id = resolved?.id || null;
        }
        if (cancelled) return;
        if (!id) {
          setResolvedClassId(null);
          setData(null);
          return;
        }
        setResolvedClassId(id);
        const result = await fetchClass(id);
        if (cancelled) return;
        setData(result);
        if (result?.is_admin && result?.school?.id) {
          try {
            const schoolData = await fetchSchool(result.school.id);
            if (!cancelled) setSchoolVenues(schoolData.venues || []);
          } catch {
            if (!cancelled) setSchoolVenues([]);
          }
        } else if (!cancelled) {
          setSchoolVenues([]);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolSlug, classSlug]);

  const weekSessions = useMemo(() => {
    const instances = data?.instances || [];
    return sessionsInWeekOffset(instances, weekOffset);
  }, [data, weekOffset]);

  const allUpcoming = useMemo(() => {
    const instances = data?.instances || [];
    return instances.filter((i) => isUpcomingInstance(i));
  }, [data]);

  const selectedInstance = useMemo(() => {
    const pool = weekSessions.length ? weekSessions : allUpcoming;
    if (!pool.length) return null;
    if (dateParam) {
      const match = pickInstanceForDate([...pool, ...allUpcoming], dateParam);
      if (match) return match;
    }
    return pool[0];
  }, [weekSessions, allUpcoming, dateParam]);

  const selectedInstanceId = selectedInstance
    ? String(selectedInstance.id || selectedInstance._id)
    : null;

  const selectedDateKey = selectedInstance
    ? classSessionDateKey(selectedInstance.effective_datetime || selectedInstance.datetime)
    : null;

  useEffect(() => {
    if (!selectedDateKey) return;
    if (dateParam !== selectedDateKey) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("instance");
        next.set("date", selectedDateKey);
        return next;
      }, { replace: true });
    }
  }, [selectedDateKey, dateParam, setSearchParams]);

  const reloadGuests = useCallback(async (instanceId) => {
    if (!instanceId) {
      setGuests([]);
      setGuestListMeta({ mode: "privacy", counts: null, preview_names: [] });
      return;
    }
    try {
      const result = await fetchInstanceRsvps(instanceId);
      setGuests(result.rsvps || []);
      setGuestListMeta({
        mode: result.mode || (result.rsvps?.length ? "full" : "privacy"),
        counts: result.counts || null,
        preview_names: result.preview_names || [],
      });
    } catch {
      setGuests([]);
      setGuestListMeta({ mode: "privacy", counts: null, preview_names: [] });
    }
  }, []);

  async function maybeOpenProfileTodos(rsvp) {
    if (!rsvp) return;
    const dancerProfile = await loadDancerProfile({ account });
    if (needsClassProfileTodos({ rsvp, account, dancerProfile })) {
      setProfileTodoRsvp(rsvp);
      setShowProfileTodos(true);
    }
  }

  async function confirmInstantRsvp(instanceId, { productId = null, firstTimer = null } = {}) {
    if (!instanceId) return null;
    setRsvpBusy(true);
    try {
      const data = await rsvpToInstance(instanceId, {
        product_id: productId,
        first_timer: firstTimer,
      });
      const elig = await reloadEligibility(instanceId);
      await reloadGuests(instanceId);
      try {
        const fresh = await fetchClass(classId);
        setData((prev) => (prev ? { ...prev, is_following: fresh.is_following } : prev));
      } catch {
        // ignore
      }
      if (data.outcome === "CONFIRMED") {
        const via = data.rsvp?.redemption_via;
        toast.success(
          via === "OFFER"
            ? "You're in! Your first class is free."
            : "You're registered!",
        );
        await maybeOpenProfileTodos(data.rsvp || elig?.my_rsvp);
      } else if (data.outcome === "PAYMENT_PENDING") {
        toast.success("Payment submitted — school will review shortly.");
      }
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    } finally {
      setRsvpBusy(false);
    }
  }

  useEffect(() => {
    if (!selectedInstanceId) {
      setEligibility(null);
      setGuests([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchInstanceRsvpEligibility(selectedInstanceId);
        if (!cancelled) setEligibility({ ...result, instance_id: selectedInstanceId });
      } catch (e) {
        if (!cancelled) {
          setEligibility({
            instance_id: selectedInstanceId,
            my_rsvp: null,
            pending_payment: null,
            can_auto_confirm: false,
            products: [],
            commerce_enabled: true,
          });
          toast.error(formatApiError(e));
        }
      }
      if (!cancelled) await reloadGuests(selectedInstanceId);
    })();
    return () => { cancelled = true; };
  }, [selectedInstanceId, account?.id, reloadGuests]);

  useEffect(() => {
    const node = heroRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroScrolledPast(!entry.isIntersecting),
      { threshold: 0.15 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [selectedInstanceId, loading]);

  function selectInstance(inst) {
    const date = classSessionDateKey(inst.effective_datetime || inst.datetime);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("instance");
      if (date) next.set("date", date);
      return next;
    }, { replace: true });
  }

  function openRsvpForm() {
    setShowRsvp(true);
  }

  function openHostAdd() {
    setShowHostAdd(true);
  }

  function clearRsvpIntentParam() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("rsvp");
      return next;
    }, { replace: true });
  }

  function goSignInForRsvp() {
    const next = encodeURIComponent(
      publicClassHref({ date: selectedDateKey, rsvp: true })
        || `${location.pathname}${location.search || ""}`,
    );
    navigate(`/login?next=${next}`);
  }

  function openRegistrationSetup() {
    setRsvpGate("admin-setup");
  }

  function eligibilityReadyForSelection() {
    return Boolean(
      selectedInstanceId
      && eligibility
      && String(eligibility.instance_id) === String(selectedInstanceId),
    );
  }

  function clearPendingRsvpClick() {
    pendingRsvpClickRef.current = false;
    setRsvpAwaitingEligibility(false);
  }

  async function proceedRsvpWithEligibility(elig) {
    if (data?.is_admin) return;
    if (!elig) return;
    const gate = resolveClassRsvpGate({
      isAdmin: false,
      signedIn: Boolean(account),
      products: elig.products || [],
      commerceEnabled: elig.commerce_enabled !== false,
      canAutoConfirm: Boolean(elig.can_auto_confirm),
    });
    if (gate) {
      setRsvpGate(gate);
      return;
    }
    const existing = elig.my_rsvp;
    if (existing && existing.status !== "CANCELLED") {
      openRsvpForm();
      return;
    }
    if (elig.pending_payment) return;
    if (elig.can_auto_confirm) {
      await confirmInstantRsvp(selectedInstanceId);
      return;
    }
    openRsvpForm();
  }

  async function handleRsvpClick() {
    if (data?.is_admin) return;
    if (!eligibilityReadyForSelection()) {
      pendingRsvpClickRef.current = true;
      setRsvpAwaitingEligibility(true);
      return;
    }
    clearPendingRsvpClick();
    await proceedRsvpWithEligibility(eligibility);
  }

  useEffect(() => {
    clearPendingRsvpClick();
  }, [selectedInstanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pendingRsvpClickRef.current || !eligibilityReadyForSelection()) return;
    clearPendingRsvpClick();
    void proceedRsvpWithEligibility(eligibility);
  }, [eligibility, selectedInstanceId, account?.id, data?.is_admin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!rsvpIntent || !account || !selectedInstanceId || !eligibility || autoRsvpHandled.current) {
      return;
    }
    if (data?.is_admin) {
      autoRsvpHandled.current = true;
      clearRsvpIntentParam();
      return;
    }
    if (eligibility.my_rsvp && eligibility.my_rsvp.status !== "CANCELLED") {
      autoRsvpHandled.current = true;
      clearRsvpIntentParam();
      return;
    }
    autoRsvpHandled.current = true;
    clearRsvpIntentParam();
    (async () => {
      if (eligibility.can_auto_confirm) {
        await confirmInstantRsvp(selectedInstanceId);
        return;
      }
      const offer = (eligibility.products || []).find(
        (p) => p.type === "OFFER" && p.offer_type === "FIRST_CLASS_FREE",
      );
      if (offer && eligibility.trial_status === "AVAILABLE") {
        await confirmInstantRsvp(selectedInstanceId, {
          productId: offer.id,
          firstTimer: true,
        });
        return;
      }
      openRsvpForm();
    })();
  }, [rsvpIntent, account, selectedInstanceId, eligibility, data?.is_admin]); // eslint-disable-line react-hooks/exhaustive-deps

  function startEdit(inst) {
    const id = inst.id || inst._id;
    setEditingId(id);
    setEditTime(inst.custom_time || timeFromIso(inst.effective_datetime || inst.datetime));
    setEditDuration(inst.custom_duration_minutes || inst.duration_minutes || 60);
    setEditVenueId(
      inst.custom_venue_id || inst.effective_venue_id || data?.venue?.id || VENUE_NONE,
    );
  }

  async function saveOverride(instanceId) {
    setSaving(true);
    try {
      await updateInstance(instanceId, {
        custom_time: editTime,
        custom_venue_id: venueIdFromSelect(editVenueId),
        custom_duration_minutes: editDuration,
      });
      toast.success("Session updated for this date only");
      setEditingId(null);
      await reload();
    } catch (e) {
      toast.error(e.message || formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function clearOverride(instanceId) {
    setSaving(true);
    try {
      await updateInstance(instanceId, { clear_overrides: true });
      toast.success("Restored weekly schedule defaults");
      setEditingId(null);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (!account || !classId || !data?.class) return;
    const action = consumePendingAuthAction("follow_class");
    if (!action || String(action.classId) !== String(classId)) return;
    if (data.is_following) return;
    let cancelled = false;
    (async () => {
      setFollowBusy(true);
      try {
        await followClass(classId);
        if (cancelled) return;
        setData((prev) => (prev ? { ...prev, is_following: true } : prev));
        toast.success("Following — you'll hear about upcoming dates.");
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setFollowBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [account, classId, data]);

  if (loading) {
    return <ClassPageLoadingSkeleton />;
  }

  if (!data?.class) {
    return (
      <div className="max-w-page mx-auto px-4 py-10 text-center">
        <p className="text-ink/70">Class not found.</p>
      </div>
    );
  }

  const { class: cls, venue, school, is_admin: isAdmin } = data;
  const ownerTheme = normalizeHostTheme(data.theme || school?.theme || DEFAULT_HOST_THEME);
  const isFollowing = Boolean(data?.is_following);
  const schoolPath = school?.slug || cls.school_id;
  const commerceOn = school?.commerce_enabled !== false;
  const venueById = Object.fromEntries(
    [...schoolVenues, venue].filter(Boolean).map((v) => [v.id, v]),
  );
  const selectedVenue = selectedInstance
    ? resolveInstanceVenue(selectedInstance, venueById) || venue
    : venue;
  const instructors = cls.instructors || data.instructors || [];
  const isCancelled = selectedInstance?.status === "CANCELLED";
  const myRsvp = eligibility?.my_rsvp || null;
  const pendingPayment = eligibility?.pending_payment || null;
  const needsRegistrationSetup = Boolean(
    isAdmin
    && commerceOn
    && eligibility
    && !(eligibility.products || []).length
    && !eligibility.can_auto_confirm,
  );
  const registrationSetupHref = schoolPath
    ? `/admin/${schoolPath}?tab=products`
    : null;
  const stickyCta = getClassStickyCtaState({
    isCancelled,
    myRsvp,
    pendingPayment,
    signedIn: Boolean(account),
    isAdmin,
    needsRegistrationSetup,
  });
  const showStickyFooter = stickyCta && heroScrolledPast;
  const weekLabel = weekOffset === 0
    ? "Next 7 days"
    : weekOffset > 0
      ? `${weekOffset} week${weekOffset === 1 ? "" : "s"} ahead`
      : `${Math.abs(weekOffset)} week${weekOffset === -1 ? "" : "s"} ago`;

  async function toggleFollow() {
    if (!classId) return;
    if (!account) {
      pendingFollowAfterAuthRef.current = true;
      stashPendingAuthAction({ type: "follow_class", classId });
      setFollowAuthOpen(true);
      return;
    }
    setFollowBusy(true);
    try {
      if (isFollowing) {
        await unfollowClass(classId);
        setData((prev) => (prev ? { ...prev, is_following: false } : prev));
        toast.success("Unfollowed — you won't get reminders for this class.");
      } else {
        await followClass(classId);
        setData((prev) => (prev ? { ...prev, is_following: true } : prev));
        toast.success("Following — you'll hear about upcoming dates.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFollowBusy(false);
    }
  }

  async function onFollowAuthSuccess() {
    setFollowAuthOpen(false);
    pendingFollowAfterAuthRef.current = false;
    consumePendingAuthAction("follow_class");
    if (!classId) return;
    setFollowBusy(true);
    try {
      await followClass(classId);
      setData((prev) => (prev ? { ...prev, is_following: true } : prev));
      toast.success("Following — you'll hear about upcoming dates.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFollowBusy(false);
    }
  }

  function onStickyClick() {
    if (stickyCta?.action === "add") return openHostAdd();
    if (stickyCta?.action === "setup") return openRegistrationSetup();
    return handleRsvpClick();
  }

  return (
    <HostThemedShell
      theme={ownerTheme}
      syncDocument={!embedded}
      className={`event-page bg-canvas font-body text-ink ${embedded ? "min-h-full" : "min-h-screen"} ${showStickyFooter ? "pb-24 sm:pb-6" : "pb-6"}`}
    >
      <DetailPageNav
        className={embedded ? "pt-3" : undefined}
        back={embedded ? (
          <ReadableNavButton
            type="button"
            onClick={() => navigate(-1)}
            data-testid="class-drawer-back"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
            Back
          </ReadableNavButton>
        ) : (
          <ReadableNavButton as={Link} to={isAdmin ? `/admin/${schoolPath}` : `/school/${schoolPath}`} data-testid="class-back-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
            School
          </ReadableNavButton>
        )}
        actions={(
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {isAdmin ? (
              <ReadableNavButton
                as={Link}
                to={`/classes/${classId}/dashboard${selectedInstanceId ? `?instance=${encodeURIComponent(selectedInstanceId)}` : ""}`}
                data-testid="class-open-dashboard"
              >
              Class Dashboard
            </ReadableNavButton>
            ) : null}
            {isAdmin ? (
              <ClassActionMenu
                editHref={schoolPath ? `/admin/${schoolPath}?tab=classes` : null}
              />
            ) : null}
            {!isAdmin ? (
              <ReadableNavButton
                type="button"
                disabled={followBusy}
                onClick={toggleFollow}
                data-testid={isFollowing ? "class-unfollow-btn" : "class-follow-btn"}
              >
                {followBusy
                  ? "…"
                  : isFollowing
                    ? "Following · Unfollow"
                    : "Follow series"}
              </ReadableNavButton>
            ) : null}
          </div>
        )}
      />

      {selectedInstance ? (
        <DetailHeroCard
          ref={heroRef}
          color={cls.cover_color || "pink"}
          testId="class-hero"
          titleTestId="class-name"
          title={cls.name}
          eyebrow={(
            <p className="font-ui text-[12px] font-medium text-white/75 break-words min-w-0">
              {school?.name ? `At ${school.name}` : "Class session"}
            </p>
          )}
          meta={(
            <>
              <DetailMetaRow icon={Calendar}>{formatChipWhen(selectedInstance)}</DetailMetaRow>
              <DetailMetaRow icon={MapPin}>{selectedVenue?.name || "Venue TBD"}</DetailMetaRow>
              {(cls.style_tag || cls.level_tag) ? (
                <DetailMetaRow>
                  {[cls.style_tag, cls.level_tag].filter(Boolean).join(" · ")}
                </DetailMetaRow>
              ) : null}
            </>
          )}
          cta={(
            <ClassHeroCta
              isCancelled={isCancelled}
              myRsvp={myRsvp}
              pendingPayment={pendingPayment}
              signedIn={Boolean(account)}
              isAdmin={isAdmin}
              needsRegistrationSetup={needsRegistrationSetup}
              entitlementPreview={eligibility?.entitlement_preview}
              rsvpBusy={rsvpBusy}
              rsvpLoading={rsvpAwaitingEligibility}
              onOpenForm={openRsvpForm}
              onRsvp={handleRsvpClick}
              onAddStudent={openHostAdd}
              onSetupRegistration={openRegistrationSetup}
            />
          )}
        >
          {cls.description && (
            <p className="text-[14px] font-normal text-ink/70 leading-relaxed whitespace-pre-line">
              <LinkifiedText linkClassName="underline underline-offset-2 text-ink hover:text-ink/70">
                {cls.description}
              </LinkifiedText>
            </p>
          )}
          {instructors.length > 0 && (
            <p className="text-[13px] font-medium text-ink/55">
              Instructor: {instructors.map((i) => i.name).join(", ")}
            </p>
          )}
          {!commerceOn && (
            <p className="text-[13px] font-medium text-ink/55">
              Pay the school directly — this app only tracks classes.
            </p>
          )}
        </DetailHeroCard>
      ) : (
        <section className="max-w-page mx-auto px-4 pt-6">
          <ReadableCard className={`${FORM_STYLE_CARD_CLASS} p-5`}>
            <h1 className="font-display text-2xl font-bold text-ink">{cls.name}</h1>
            <FieldHint className="mt-2">No upcoming sessions right now.</FieldHint>
          </ReadableCard>
        </section>
      )}

      <section className="max-w-page mx-auto px-4 pt-5 space-y-4">
        {selectedInstance && (guestListMeta.mode === "privacy"
          ? (guestListMeta.counts?.total > 0)
          : guests.length > 0) && (
          <ReadableCard className={`${FORM_STYLE_CARD_CLASS} space-y-3`} data-testid="class-guest-list">
            <h2 className="font-ui font-semibold">
              Who&apos;s coming (
              {guestListMeta.mode === "privacy"
                ? (guestListMeta.counts?.total || 0)
                : guests.length}
              )
            </h2>
            {guestListMeta.mode === "privacy" ? (
              <div className="space-y-2" data-testid="class-guest-list-privacy">
                {formatRoleCounts(guestListMeta.counts) ? (
                  <p className="font-ui text-[15px] font-medium text-ink/80">
                    {formatRoleCounts(guestListMeta.counts)}
                  </p>
                ) : null}
                {formatPreviewNames(
                  guestListMeta.preview_names,
                  guestListMeta.counts?.total || 0,
                ) ? (
                  <p className="font-ui text-[13px] text-ink/60">
                    {formatPreviewNames(
                      guestListMeta.preview_names,
                      guestListMeta.counts?.total || 0,
                    )}
                  </p>
                ) : null}
                <FieldHint>
                  Full names are visible to class members with an active pack or membership.
                </FieldHint>
              </div>
            ) : (
              <ul className="divide-y divide-black/[0.08]">
                {guests.map((g) => (
                  <li key={g.id} className="py-3 first:pt-1 text-sm flex flex-col gap-0.5">
                    <span className="font-medium">
                      {g.name || "Dancer"}
                      {g.role ? (
                        <span className="ml-2 font-ui font-medium text-[12px] text-ink/50">{g.role}</span>
                      ) : null}
                      {isAdmin && g.hide_name ? (
                        <span className="ml-2 font-ui font-medium text-[12px] text-ink/60">(hidden on public list)</span>
                      ) : null}
                    </span>
                    {isAdmin && g.phone ? (
                      <span className="text-[12px] text-ink/50">{g.phone}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </ReadableCard>
        )}

        <ReadableCard className={`${FORM_STYLE_CARD_CLASS} space-y-3`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-ui font-semibold">Upcoming dates</h2>
            <div className="flex items-center gap-1" data-testid="class-week-nav">
              <ReadableNavButton type="button" onClick={() => setWeekOffset((n) => n - 1)} data-testid="week-prev">
                Prev
              </ReadableNavButton>
              <ReadableNavButton
                type="button"
                variant={weekOffset === 0 ? "primary" : undefined}
                onClick={() => setWeekOffset(0)}
                data-testid="week-current"
              >
                This week
              </ReadableNavButton>
              <ReadableNavButton type="button" onClick={() => setWeekOffset((n) => n + 1)} data-testid="week-next">
                Next
              </ReadableNavButton>
            </div>
          </div>
          <FieldHint>{weekLabel}</FieldHint>
          {isAdmin && (
            <FieldHint>Customize a single date without changing the weekly template.</FieldHint>
          )}
          {weekSessions.length ? (
            <ul className="divide-y divide-black/[0.08]">
              {weekSessions.map((inst) => {
                const id = String(inst.id || inst._id);
                const editing = editingId === id;
                const selected = id === selectedInstanceId;
                const effVenue = resolveInstanceVenue(inst, venueById) || venue;
                return (
                  <li
                    key={id}
                    className={`py-3 first:pt-2 space-y-3 ${selected ? "bg-orchid/10 -mx-1 px-1 rounded-md2" : ""}`}
                    data-testid={`class-instance-${id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        className="min-w-0 text-left"
                        onClick={() => selectInstance(inst)}
                      >
                        <div className="text-sm font-medium">{formatSessionRange(inst)}</div>
                        {effVenue ? (
                          <p className="text-[12px] text-ink/60 mt-0.5 flex items-center gap-1">
                            <MapPin size={12} aria-hidden />
                            {effVenue.name}
                          </p>
                        ) : (
                          <p className="text-[12px] text-ink/60 mt-0.5">Venue TBD — contact host</p>
                        )}
                        {inst.has_overrides && (
                          <span className="inline-block mt-1 text-[11px] font-semibold uppercase tracking-wide text-accent">
                            Custom time/venue
                          </span>
                        )}
                      </button>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {!selected && (
                          <button
                            type="button"
                            onClick={() => selectInstance(inst)}
                            className="inline-flex min-h-[44px] items-center px-1 text-sm font-semibold text-plum"
                          >
                            View
                          </button>
                        )}
                        {isAdmin && !editing && (
                          <button
                            type="button"
                            onClick={() => startEdit(inst)}
                            className="inline-flex min-h-[44px] items-center px-1 text-sm font-semibold text-plum"
                            data-testid={`customize-instance-${id}`}
                          >
                            Customize
                          </button>
                        )}
                      </div>
                    </div>

                    {editing && (
                      <div className="space-y-3 border-t border-black/[0.08] pt-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label className="block">
                            <FormLabel className="block mb-2">Start time</FormLabel>
                            <input
                              type="time"
                              className="field"
                              value={editTime}
                              onChange={(e) => setEditTime(e.target.value)}
                              data-testid="instance-custom-time"
                            />
                          </label>
                          <label className="block">
                            <FormLabel className="block mb-2">Duration</FormLabel>
                            <select
                              className="field"
                              value={editDuration}
                              onChange={(e) => setEditDuration(Number(e.target.value))}
                              data-testid="instance-custom-duration"
                            >
                              {DURATION_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                              {!DURATION_OPTIONS.some((o) => o.value === editDuration) && (
                                <option value={editDuration}>{editDuration} min</option>
                              )}
                            </select>
                            <FieldHint className="mt-1.5">
                              Ends at {endTimeLabel(editTime, editDuration)}
                            </FieldHint>
                          </label>
                        </div>
                        <VenueInput
                          mode="school"
                          venues={schoolVenues}
                          venueId={editVenueId}
                          onVenueIdChange={setEditVenueId}
                        />
                        <div className="flex flex-wrap gap-2">
                          <ReadableNavButton
                            type="button"
                            variant="primary"
                            disabled={saving}
                            onClick={() => saveOverride(id)}
                            data-testid="instance-save-override"
                          >
                            {saving ? "Saving…" : "Save for this date"}
                          </ReadableNavButton>
                          {inst.has_overrides && (
                            <ReadableNavButton
                              type="button"
                              disabled={saving}
                              onClick={() => clearOverride(id)}
                              data-testid="instance-clear-override"
                            >
                              Clear overrides
                            </ReadableNavButton>
                          )}
                          <ReadableNavButton
                            type="button"
                            disabled={saving}
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </ReadableNavButton>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-ink/50">No sessions in this week.</p>
          )}
        </ReadableCard>
      </section>

      <EventStickyCta
        cta={stickyCta}
        visible={Boolean(showStickyFooter)}
        onOpenForm={onStickyClick}
        onOpenInviteForm={onStickyClick}
        requiresInvite={false}
        loading={rsvpBusy || rsvpAwaitingEligibility}
      />

      {showRsvp && !isAdmin && (
        <Suspense fallback={null}>
          <ClassRsvpModal
            className={cls.name}
            sessionLabel={selectedInstance ? formatSessionRange(selectedInstance) : ""}
            eligibility={eligibility || { instance_id: selectedInstanceId, products: [], can_auto_confirm: false }}
            currency={school?.currency || "INR"}
            onClose={() => setShowRsvp(false)}
            onDone={async (result) => {
              const elig = await reloadEligibility(selectedInstanceId);
              await reloadGuests(selectedInstanceId);
              const rsvp = result?.rsvp || elig?.my_rsvp;
              if (result?.outcome === "CONFIRMED" || (rsvp && rsvp.status !== "CANCELLED")) {
                await maybeOpenProfileTodos(rsvp);
              }
            }}
          />
        </Suspense>
      )}

      {showHostAdd && selectedInstanceId && (
        <Suspense fallback={null}>
          <HostAddStudentModal
            instanceId={selectedInstanceId}
            className={cls.name}
            schoolName={school?.name || ""}
            sessionLabel={selectedInstance ? formatSessionRange(selectedInstance) : ""}
            onClose={() => setShowHostAdd(false)}
            onDone={async () => {
              await reloadGuests(selectedInstanceId);
            }}
          />
        </Suspense>
      )}

      {rsvpGate && (
        <Suspense fallback={null}>
          <ClassRsvpGateModal
            variant={rsvpGate}
            className={cls.name}
            contacts={eligibility?.contacts}
            setupHref={registrationSetupHref}
            onClose={() => setRsvpGate(null)}
            onContinue={() => {
              setRsvpGate(null);
              goSignInForRsvp();
            }}
          />
        </Suspense>
      )}

      {showProfileTodos && profileTodoRsvp && (
        <Suspense fallback={null}>
          <ClassRsvpTodosDrawer
            rsvp={profileTodoRsvp}
            account={account}
            onClose={() => setShowProfileTodos(false)}
            onDone={async () => {
              await reloadEligibility(selectedInstanceId);
              await reloadGuests(selectedInstanceId);
            }}
          />
        </Suspense>
      )}

      <AuthModal
        open={followAuthOpen}
        onClose={() => {
          pendingFollowAfterAuthRef.current = false;
          setFollowAuthOpen(false);
        }}
        onSuccess={onFollowAuthSuccess}
        title="Sign in to follow this class"
        subtitle="Following saves reminders to your account so upcoming dates show up after you come back."
      />
    </HostThemedShell>
  );
}
